import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Modal } from '../components/Modal';

export function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [referralCode, setReferralCode] = useState(() => {
    // Récupérer le code de parrainage du localStorage lors de l'initialisation
    return localStorage.getItem('referral_code') || '';
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth() as { 
    register: (name: string, email: string, password: string, extraData?: any) => Promise<{ id: string; email: string | null; username: string; }> 
  };
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usePhone, setUsePhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Effet pour mettre à jour le code si le localStorage change
  useEffect(() => {
    const storedCode = localStorage.getItem('referral_code');
    if (storedCode) {
      setReferralCode(storedCode);
    }
  }, []);

  // Fonction utilitaire pour transformer le numéro en pseudo-email
  function phoneToPseudoEmail(phone: string) {
    return `${phone.replace(/\D/g, '')}@magraine.app`;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setIsLoading(false);
      return;
    }

    if (!acceptTerms) {
      setError('Vous devez accepter la politique de confidentialité et les termes d\'utilisation');
      setIsLoading(false);
      return;
    }

    let finalEmail = email;
    let extraData: any = {};
    if (usePhone) {
      if (!phone.match(/^\+?\d{8,15}$/)) {
        setError('Numéro de téléphone invalide');
        setIsLoading(false);
        return;
      }
      finalEmail = phoneToPseudoEmail(phone);
      extraData.phone = phone;
    }

    try {
      await register(name, finalEmail, password, extraData);
      // Nettoyer le code de parrainage après une inscription réussie
      localStorage.removeItem('referral_code');
      navigate('/');
    } catch (error: any) {
      console.error('Erreur lors de l\'inscription:', error);
      setError(error.message || 'Une erreur est survenue lors de l\'inscription');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img 
            src="/Nouveau logo magraine_00000.png"
            alt="magraine" 
            className="w-[450px] h-[75px] object-contain"
          />
          <h1 className="text-3xl font-bold text-white/50 mb-2">Inscription</h1>
        </div>

        <div className="flex justify-center mb-2 space-x-2">
          <button
            type="button"
            className={`px-4 py-2 rounded-l-xl ${!usePhone ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-300'}`}
            onClick={() => setUsePhone(false)}
          >
            Email
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-r-xl ${usePhone ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-300'}`}
            onClick={() => setUsePhone(true)}
          >
            Téléphone
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-400 text-sm mb-2">Nom d'utilisateur</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
              required
            />
          </div>

          {!usePhone ? (
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-green-500/50"
                  required={!usePhone}
                  disabled={usePhone}
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-gray-400 text-sm mb-2">Numéro de téléphone</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-green-500/50"
                  required={usePhone}
                  disabled={!usePhone}
                  placeholder="Ex: +237612345678"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-gray-400 text-sm mb-2">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-green-500/50"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Confirmer le mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-3 text-white focus:outline-none focus:border-green-500/50"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Code de parrainage (optionnel)</label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              readOnly={!!localStorage.getItem('referral_code')}
              className={`w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50 ${
                localStorage.getItem('referral_code') ? 'opacity-50' : ''
              }`}
            />
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="terms"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="terms" className="text-sm text-gray-400">
              J'ai lu et j'accepte la{' '}
              <button
                type="button"
                onClick={() => navigate('/privacy')}
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                politique de confidentialité
              </button>
              {' '}et les{' '}
              <button
                type="button"
                onClick={() => navigate('/terms')}
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                termes d'utilisation
              </button>
            </label>
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center font-semibold">{error}</p>
          )}

          <button
            type="submit"
            className={`w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 rounded-xl transition-colors ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isLoading}
          >
            {isLoading ? 'Inscription...' : "S'inscrire"}
          </button>
        </form>
        {showErrorModal && error && (
          <Modal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)}>
            <div className="p-6 text-center">
              <h2 className="text-lg font-bold mb-2 text-red-500">Erreur d'inscription</h2>
              <p className="mb-4 text-gray-200">{error}</p>
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-xl font-semibold"
                onClick={() => setShowErrorModal(false)}
              >
                Fermer
              </button>
            </div>
          </Modal>
        )}

        <p className="text-center text-gray-400">
          Déjà un compte ?{' '}
          <Link to="/login" className="text-green-400 hover:text-green-300 transition-colors">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}